import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  BarChart3,
  Camera,
  Check,
  Copy,
  Download,
  EyeOff,
  Eye,
  FileDown,
  QrCode,
  Users,
  MessageSquare,
  Play,
  ImagePlus,
  Loader2,
  Lock,
  LogOut,
  Pencil,
  Plus,
  Printer,
  Star,
  Target,
  Unlink,
  Volume2,
} from 'lucide-react';
import { partnerAPI, paymentsAPI } from '../services/api';
import { getImageUrl } from '../config/runtime';
import { useAuth } from '../contexts/AuthContext';
// The exact same create/edit modal the /starters page uses — B2B owners get
// the proven B2C flow, the dashboard only adds auto-attach on top.
import EditProjectModal from '../components/EditProjectModal';
import LanguageSwitcher from '../components/LanguageSwitcher';

/**
 * /p/:slug/dashboard — the partner ops surface.
 *
 * Everything a venue needs from us, in one place: live session stats, the
 * print-ready QR per adventure (rendered on demand against THIS origin, so
 * dev shows dev QRs and prod shows prod QRs — no provisioning step), the
 * copyable play links, and gallery moderation. Access: platform admin OR
 * the venue's own account (partners.ownerUserId — plan_b2b_self_serve §4.1);
 * the API is the gate, this page just renders what it's allowed to see.
 */

// Public plan card (plan_b2b_self_serve.md §3) — keys map to STRIPE_PRICE_IDS
// entries; changing prices = changing Stripe dashboard + this display copy.
// Billed by venue size, never session meters (caps are invisible ceilings).
const PARTNER_PLANS = [
  { key: 'plan_partner_starter', name: 'Starter', price: '249', scopeKey: 'plans.starterScope' },
  { key: 'plan_partner_standard', name: 'Standard', price: '490', scopeKey: 'plans.standardScope' },
  { key: 'plan_partner_pro', name: 'Pro', price: '990', scopeKey: 'plans.proScope' },
];

// Slim account chrome for the venue surface: the full app header (projects,
// tokens, …) would be noise for an operator, but "who am I and how do I get
// out" must exist. Rendered on every dashboard state incl. access-denied,
// where switching accounts is exactly what the visitor needs.
const AccountBar = ({ user, onLogout }) => {
  const { t } = useTranslation('dashboard');
  const identity = user?.email || user?.username || user?.firstName || 'Account';
  const initial = (user?.firstName || user?.username || user?.email || '?')
    .charAt(0)
    .toUpperCase();
  return (
    <div className="border-b border-white/5 bg-gray-950/80 backdrop-blur sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
        <Link to="/business" className="flex-shrink-0">
          <img src="/logo_white.webp" alt="OKIO" className="h-4 w-auto" />
        </Link>
        <div className="flex items-center gap-3 min-w-0">
          <LanguageSwitcher />
          {user ? (
            <>
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                  {initial}
                </span>
                <span className="text-white/40 text-xs truncate hidden sm:block max-w-[200px]">
                  {identity}
                </span>
              </div>
              <button
                onClick={onLogout}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white/60 hover:text-white text-xs font-semibold transition-colors flex-shrink-0"
              >
                <LogOut size={12} />
                {t('account.signOut')}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white/60 hover:text-white text-xs font-semibold transition-colors"
            >
              {t('account.signIn')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

// DIY half of the guide pipeline: guides the owner built themselves in the
// normal editor, ready to be put on the venue page (attach = set partnerId,
// gated server-side by plan guide caps). Hidden when there's nothing to add.
const AddGuidesSection = ({ slug, refreshToken, onAttached }) => {
  const { t } = useTranslation('dashboard');
  const [starters, setStarters] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    partnerAPI
      .listAttachableStarters(slug)
      .then((response) => setStarters(response.data.data.starters || []))
      .catch(() => {});
  }, [slug, refreshToken]);

  if (starters.length === 0) return null;

  const attach = async (starter) => {
    setBusyId(starter.id);
    try {
      await partnerAPI.attachStarter(slug, starter.id);
      toast.success(t('otherGuides.attached', { name: starter.name }));
      setStarters((previous) => previous.filter((item) => item.id !== starter.id));
      onAttached();
    } catch (err) {
      console.error('[PartnerDashboard] Attach failed:', err);
      toast.error(err?.response?.data?.detail || t('otherGuides.attachFailed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="mb-12">
      <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-1">
        {t('otherGuides.title')}
      </h2>
      <p className="text-white/40 text-xs mb-4">{t('otherGuides.subtitle')}</p>
      <div className="space-y-3">
        {starters.map((starter) => (
          <div
            key={starter.id}
            className="rounded-2xl bg-gray-900 border border-white/10 p-4 flex items-center gap-4"
          >
            {starter.images?.[0] && (
              <img
                src={getImageUrl(starter.images[0])}
                alt=""
                className="w-11 h-11 rounded-xl object-cover flex-shrink-0 bg-gray-800"
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{starter.name}</p>
              {starter.description && (
                <p className="text-white/40 text-xs truncate">{starter.description}</p>
              )}
            </div>
            <button
              onClick={() => attach(starter)}
              disabled={busyId === starter.id}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold transition-colors disabled:opacity-50 flex-shrink-0"
            >
              {busyId === starter.id ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Plus size={13} />
              )}
              {t('otherGuides.addToPage')}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl bg-gray-900 border border-white/10 p-5">
    <div className="flex items-center gap-2 text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
      <Icon size={14} />
      {label}
    </div>
    <div className="text-3xl font-bold text-white">{value}</div>
  </div>
);

// One block serves both QR kinds: per-starter ("start this adventure" — the
// on-location sticker) and venue ("what shall we do today?" — the hotel-lobby
// poster pointing at /p/{slug}). `starterId` null = venue variant.
const QrBlock = ({ slug, starterId = null, name, partnerName, link, posterCta, posterSub, children }) => {
  const { t } = useTranslation('dashboard');
  const [qrUrl, setQrUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [enlarged, setEnlarged] = useState(false);

  const playLink = link;

  // Close the enlarged QR with Escape
  useEffect(() => {
    if (!enlarged) return undefined;
    const onKey = (event) => event.key === 'Escape' && setEnlarged(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enlarged]);

  // Print-ready A-series poster: big QR + name + one-line instructions.
  const printPoster = () => {
    if (!qrUrl) return;
    const posterWindow = window.open('', '_blank');
    if (!posterWindow) {
      toast.error(t('qr.popupBlocked'));
      return;
    }
    posterWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<title>${name} — QR poster</title>
<style>
  @page { margin: 2cm; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, sans-serif; text-align: center; color: #111; margin: 0; padding: 3rem 2rem; }
  .partner { font-size: 1rem; letter-spacing: 0.2em; text-transform: uppercase; color: #666; margin-bottom: 1rem; }
  h1 { font-size: 2.2rem; margin: 0 0 2.5rem; }
  img { width: 62vw; max-width: 480px; }
  .cta { font-size: 1.5rem; font-weight: 700; margin-top: 2.5rem; }
  .sub { font-size: 1rem; color: #555; margin-top: 0.75rem; }
  .link { font-size: 0.8rem; color: #999; margin-top: 2.5rem; word-break: break-all; }
</style>
</head>
<body>
  <div class="partner">${partnerName || ''}</div>
  <h1>${name}</h1>
  <img src="${qrUrl}" alt="QR code" />
  <div class="cta">${posterCta || 'Scannen &amp; losspielen'}</div>
  <div class="sub">${posterSub || 'Kostenlos &middot; keine App n&ouml;tig &middot; startet direkt im Browser'}</div>
  <div class="link">${playLink}</div>
  <script>window.onload = function () { window.print(); };</script>
</body>
</html>`);
    posterWindow.document.close();
  };

  useEffect(() => {
    let objectUrl = null;
    const load = async () => {
      try {
        const response = starterId
          ? await partnerAPI.getStarterQr(slug, starterId, window.location.origin)
          : await partnerAPI.getVenueQr(slug, window.location.origin);
        objectUrl = URL.createObjectURL(response.data);
        setQrUrl(objectUrl);
      } catch (err) {
        console.error('[PartnerDashboard] QR load failed:', err);
      }
    };
    load();
    return () => objectUrl && URL.revokeObjectURL(objectUrl);
  }, [slug, starterId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(playLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // Clipboard can be unavailable (http, permissions) — the link is visible anyway
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-5 items-start">
      <button
        type="button"
        onClick={() => qrUrl && setEnlarged(true)}
        title={t('qr.enlargeHint')}
        className="group relative w-20 h-20 rounded-xl bg-white p-1.5 flex-shrink-0 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-emerald-400"
      >
        {qrUrl ? (
          <img src={qrUrl} alt={`QR — ${name}`} className="w-full h-full" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <QrCode className="text-gray-300" size={28} />
          </div>
        )}
        <span className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/10 transition-colors" />
      </button>

      {/* Enlarged QR — big enough to scan across a table (demos, onboarding) */}
      {enlarged && qrUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-6"
          onClick={() => setEnlarged(false)}
        >
          <div
            className="bg-white rounded-3xl p-6 max-w-[90vw]"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={qrUrl}
              alt={`QR — ${name}`}
              className="w-[min(70vw,420px)] h-auto"
            />
          </div>
          <p className="mt-5 text-white font-semibold text-lg text-center">{name}</p>
          <p className="mt-1 text-white/50 text-xs font-mono break-all text-center max-w-md">
            {playLink}
          </p>
          <p className="mt-4 text-white/40 text-xs">{t('qr.closeHint')}</p>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-white/40 text-xs font-mono break-all mb-3">{playLink}</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? t('qr.copied') : t('qr.copyLink')}
          </button>
          {qrUrl && (
            <a
              href={qrUrl}
              download={`okio-qr-${slug}${starterId ? `-${starterId.slice(0, 8)}` : '-venue'}.png`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
            >
              <Download size={13} />
              {t('qr.download')}
            </a>
          )}
          {qrUrl && (
            <button
              onClick={printPoster}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
            >
              <Printer size={13} />
              {t('qr.printPoster')}
            </button>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

const AutoImagesToggle = ({ slug, starter, onUpdated, entitled = true }) => {
  const { t } = useTranslation('dashboard');
  const [enabled, setEnabled] = useState(Boolean(starter.autoInstructionImages));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(Boolean(starter.autoInstructionImages));
  }, [starter.autoInstructionImages]);

  const toggle = async () => {
    if (!entitled) return;
    const next = !enabled;
    setSaving(true);
    try {
      const response = await partnerAPI.updateStarterSettings(slug, starter.starterId, {
        auto_instruction_images: next,
      });
      const saved = Boolean(response.data.data.autoInstructionImages);
      setEnabled(saved);
      onUpdated?.(starter.starterId, saved);
    } catch (err) {
      console.error('[PartnerDashboard] Auto images toggle failed:', err);
      if (err.response?.data?.detail?.code === 'tier_required') {
        toast.error(t('entitlement.imagesHint'));
      } else {
        toast.error(t('autoImages.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Tier-locked (pricing v2): rendered off regardless of the stored setting —
  // runtime gates ignore it below the entitled tier anyway.
  const shown = entitled ? enabled : false;

  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium mb-1">
            <ImagePlus size={16} className="text-indigo-400 flex-shrink-0" />
            {t('autoImages.title')}
          </div>
          <p className="text-white/40 text-xs leading-relaxed">{t('autoImages.subtitle')}</p>
          {!entitled && (
            <p className="mt-1.5 flex items-center gap-1.5 text-amber-300/90 text-xs">
              <Lock size={11} className="flex-shrink-0" />
              {t('entitlement.imagesHint')}
            </p>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={shown}
          disabled={saving || !entitled}
          onClick={toggle}
          className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
            shown ? 'bg-emerald-500' : 'bg-white/20'
          } ${saving ? 'opacity-60 cursor-wait' : ''} ${
            !entitled ? 'opacity-40 cursor-not-allowed' : ''
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform flex items-center justify-center ${
              shown ? 'translate-x-5' : 'translate-x-0'
            }`}
          >
            {saving && <Loader2 size={10} className="animate-spin text-gray-500" />}
          </span>
        </button>
      </div>
    </div>
  );
};

// Per-guide voice layer kill switch (default ON). Turning it off hides the
// listen buttons and voice mode for NEW sessions of this guide and makes the
// TTS endpoint refuse synthesis for it. When ON, a sub-toggle lets the venue
// pre-enable voice mode for visitors who never made their own choice
// (headphone experiences) — a visitor's explicit toggle always wins.
const VoiceToggle = ({ slug, starter, onUpdated, entitled = true }) => {
  const { t } = useTranslation('dashboard');
  const [enabled, setEnabled] = useState(starter.voiceEnabled !== false);
  const [defaultOn, setDefaultOn] = useState(starter.voiceDefaultOn === true);
  const [saving, setSaving] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);

  useEffect(() => {
    setEnabled(starter.voiceEnabled !== false);
    setDefaultOn(starter.voiceDefaultOn === true);
  }, [starter.voiceEnabled, starter.voiceDefaultOn]);

  const toggle = async () => {
    if (!entitled) return;
    const next = !enabled;
    setSaving(true);
    try {
      const response = await partnerAPI.updateStarterSettings(slug, starter.starterId, {
        voice_enabled: next,
      });
      const saved = response.data?.data?.voiceEnabled !== false;
      setEnabled(saved);
      onUpdated?.(starter.starterId, { voiceEnabled: saved });
      toast.success(saved ? t('voice.onToast') : t('voice.offToast'));
    } catch (err) {
      console.error('[PartnerDashboard] Voice toggle failed:', err);
      if (err.response?.data?.detail?.code === 'tier_required') {
        toast.error(t('entitlement.voiceHint'));
      } else {
        toast.error(t('voice.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleDefault = async () => {
    const next = !defaultOn;
    setSavingDefault(true);
    try {
      const response = await partnerAPI.updateStarterSettings(slug, starter.starterId, {
        voice_default_on: next,
      });
      const saved = response.data?.data?.voiceDefaultOn === true;
      setDefaultOn(saved);
      onUpdated?.(starter.starterId, { voiceDefaultOn: saved });
      toast.success(saved ? t('voice.defaultOnToast') : t('voice.defaultOffToast'));
    } catch (err) {
      console.error('[PartnerDashboard] Voice default toggle failed:', err);
      toast.error(t('voice.saveFailed'));
    } finally {
      setSavingDefault(false);
    }
  };

  // Tier-locked (pricing v2): rendered off regardless of the stored setting —
  // TTS refuses synthesis below the entitled tier anyway.
  const shown = entitled ? enabled : false;

  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium mb-1">
            <Volume2 size={16} className="text-sky-400 flex-shrink-0" />
            {t('voice.title')}
          </div>
          <p className="text-white/40 text-xs leading-relaxed">{t('voice.subtitle')}</p>
          {!entitled && (
            <p className="mt-1.5 flex items-center gap-1.5 text-amber-300/90 text-xs">
              <Lock size={11} className="flex-shrink-0" />
              {t('entitlement.voiceHint')}
            </p>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={shown}
          disabled={saving || !entitled}
          onClick={toggle}
          className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
            shown ? 'bg-emerald-500' : 'bg-white/20'
          } ${saving ? 'opacity-60 cursor-wait' : ''} ${
            !entitled ? 'opacity-40 cursor-not-allowed' : ''
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform flex items-center justify-center ${
              shown ? 'translate-x-5' : 'translate-x-0'
            }`}
          >
            {saving && <Loader2 size={10} className="animate-spin text-gray-500" />}
          </span>
        </button>
      </div>
      {shown && (
        <div className="mt-3 ml-6 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-white/60 text-xs font-medium mb-0.5">
              {t('voice.defaultTitle')}
            </div>
            <p className="text-white/35 text-[11px] leading-relaxed">
              {t('voice.defaultSubtitle')}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={defaultOn}
            disabled={savingDefault}
            onClick={toggleDefault}
            className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors ${
              defaultOn ? 'bg-sky-500' : 'bg-white/20'
            } ${savingDefault ? 'opacity-60 cursor-wait' : ''}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform flex items-center justify-center ${
                defaultOn ? 'translate-x-4' : 'translate-x-0'
              }`}
            >
              {savingDefault && <Loader2 size={9} className="animate-spin text-gray-500" />}
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

const TIME_WINDOWS = [
  { days: 7, labelKey: 'windows.d7' },
  { days: 30, labelKey: 'windows.d30' },
  { days: null, labelKey: 'windows.all' },
];

// Per-guide "join my run" toggle (default ON) — group play is an
// experience-level decision, so it lives on the guide card, not the plan.
// Companions are real visitor sessions and count toward the monthly budget.
const GroupPlayToggle = ({ slug, starter, onUpdated }) => {
  const { t } = useTranslation('dashboard');
  const [enabled, setEnabled] = useState(starter.allowCompanionJoin !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(starter.allowCompanionJoin !== false);
  }, [starter.allowCompanionJoin]);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      const response = await partnerAPI.updateStarterSettings(slug, starter.starterId, {
        allow_companion_join: next,
      });
      const saved = response.data?.data?.allowCompanionJoin !== false;
      setEnabled(saved);
      onUpdated?.(starter.starterId, saved);
      toast.success(saved ? t('groupPlay.onToast') : t('groupPlay.offToast'));
    } catch (err) {
      console.error('[PartnerDashboard] Group play toggle failed:', err);
      toast.error(t('groupPlay.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium mb-1">
            <Users size={16} className="text-emerald-400 flex-shrink-0" />
            {t('groupPlay.title')}
          </div>
          <p className="text-white/40 text-xs leading-relaxed">{t('groupPlay.subtitle')}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={saving}
          onClick={toggle}
          className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
            enabled ? 'bg-emerald-500' : 'bg-white/20'
          } ${saving ? 'opacity-60 cursor-wait' : ''}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform flex items-center justify-center ${
              enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          >
            {saving && <Loader2 size={10} className="animate-spin text-gray-500" />}
          </span>
        </button>
      </div>
    </div>
  );
};

// Station drop-off funnel (plan_real_guide_gaps.md workstream A). Collapsed
// by default and fetched lazily on first expand — one extra request per guide
// only when the owner actually asks for it. Aggregate-only by design; the
// backend suppresses the chart below a minimum run count.
const StationFunnel = ({ slug, starterId }) => {
  const { t } = useTranslation('dashboard');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [funnel, setFunnel] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || funnel || loading) return;
    setLoading(true);
    try {
      const response = await partnerAPI.getStarterFunnel(slug, starterId);
      setFunnel(response.data?.data || null);
    } catch (err) {
      console.error('[PartnerDashboard] Funnel load failed:', err);
      toast.error(t('funnel.loadFailed'));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium transition-colors"
      >
        <BarChart3 size={16} className="text-emerald-400 flex-shrink-0" />
        {open ? t('funnel.hide') : t('funnel.show')}
        {loading && <Loader2 size={13} className="animate-spin text-white/40" />}
      </button>
      {open && funnel && (
        <div className="mt-4">
          {funnel.suppressed ? (
            <p className="text-white/40 text-xs leading-relaxed">
              {t('funnel.tooFewRuns', { min: funnel.minRuns })}
            </p>
          ) : funnel.stations.length === 0 ? (
            <p className="text-white/40 text-xs leading-relaxed">{t('funnel.noStations')}</p>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-white/50 mb-3">
                <span>{t('funnel.runsStarted', { count: funnel.runs })}</span>
                <span>{t('funnel.runsFinished', { count: funnel.finished ?? 0 })}</span>
              </div>
              <div className="space-y-1.5">
                {funnel.stations.map((station) => {
                  const pct = funnel.runs > 0
                    ? Math.round((station.completions / funnel.runs) * 100)
                    : 0;
                  return (
                    <div key={station.orderIndex} className="flex items-center gap-3">
                      <span className="w-5 text-right text-[11px] text-white/40 flex-shrink-0 tabular-nums">
                        {station.orderIndex + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-xs text-white/70 truncate">{station.name}</span>
                          <span className="text-[11px] text-white/40 tabular-nums flex-shrink-0">
                            {pct}%
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-500/70"
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-white/30 text-[11px] leading-relaxed mt-3">
                {t('funnel.hint')}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PlanBilling = ({ slug, tier }) => {
  const { t } = useTranslation('dashboard');
  const [busyKey, setBusyKey] = useState(null);
  const returnPath = `/p/${slug}/dashboard`;
  // "pilot" = unpaid/manual tier → show the subscribe row; paid tiers get
  // the Stripe portal (change card, download invoices, cancel).
  const isSubscribed = tier && tier !== 'pilot';

  const subscribe = async (priceKey) => {
    setBusyKey(priceKey);
    try {
      const response = await paymentsAPI.createStripeCheckout({
        priceKey,
        mode: 'subscription',
        successUrl: returnPath,
        cancelUrl: returnPath,
      });
      window.location.href = response.data.data.url;
    } catch (err) {
      console.error('[PartnerDashboard] Checkout failed:', err);
      toast.error(t('billing.checkoutFailed'));
      setBusyKey(null);
    }
  };

  const openPortal = async () => {
    setBusyKey('portal');
    try {
      const response = await paymentsAPI.createStripePortal({ returnUrl: returnPath });
      window.location.href = response.data.data.url;
    } catch (err) {
      console.error('[PartnerDashboard] Portal failed:', err);
      toast.error(t('billing.portalFailed'));
      setBusyKey(null);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-white/80 text-sm font-medium">{t('billing.title')}</div>
        {isSubscribed && (
          <button
            onClick={openPortal}
            disabled={busyKey === 'portal'}
            className="px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {busyKey === 'portal' ? t('billing.opening') : t('billing.manage')}
          </button>
        )}
      </div>
      {!isSubscribed && (
        <>
          <p className="text-white/40 text-xs mb-3">{t('billing.pickPlan')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PARTNER_PLANS.map((plan) => (
              <button
                key={plan.key}
                onClick={() => subscribe(plan.key)}
                disabled={Boolean(busyKey)}
                className="rounded-2xl border border-white/10 hover:border-emerald-400/50 bg-white/5 p-4 text-left transition-colors disabled:opacity-50"
              >
                <div className="text-white text-sm font-semibold mb-0.5">
                  {plan.name}
                </div>
                <div className="text-emerald-300 text-lg font-bold">
                  €{plan.price}
                  <span className="text-white/40 text-xs font-normal">{t('plans.perMonth')}</span>
                </div>
                <div className="text-white/40 text-[11px] mt-1">{t(plan.scopeKey)}</div>
                {busyKey === plan.key && (
                  <Loader2 size={13} className="animate-spin text-emerald-300 mt-2" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Labels/placeholders live in the dashboard namespace: request.fields.{key}
const GUIDE_REQUEST_FIELDS = [
  { key: 'experience', required: true, textarea: true },
  { key: 'audience' },
  { key: 'duration' },
  { key: 'language' },
  { key: 'facts', textarea: true },
];

const GuideRequestSection = ({ slug }) => {
  const { t } = useTranslation('dashboard');
  const [fields, setFields] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    partnerAPI
      .listGuideRequests(slug)
      .then((response) => setRequests(response.data.data.requests || []))
      .catch(() => {});
  }, [slug]);

  const submit = async () => {
    if (!fields.experience?.trim()) {
      toast.error(t('request.needExperience'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await partnerAPI.createGuideRequest(slug, fields);
      setRequests((previous) => [...previous, response.data.data.request]);
      setFields({});
      toast.success(t('request.received'));
    } catch (err) {
      console.error('[PartnerDashboard] Guide request failed:', err);
      toast.error(err?.response?.data?.detail || t('request.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mb-12 rounded-3xl bg-gray-900 border border-white/10 p-6">
      <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-1">
        {t('request.title')}
      </h2>
      <p className="text-white/40 text-xs mb-5">{t('request.subtitle')}</p>
      <div className="space-y-4">
        {GUIDE_REQUEST_FIELDS.map((field) => (
          <div key={field.key}>
            <label className="block text-white/60 text-xs font-semibold mb-1.5">
              {t(`request.fields.${field.key}.label`)}
              {field.required && <span className="text-emerald-400"> *</span>}
            </label>
            {field.textarea ? (
              <textarea
                value={fields[field.key] || ''}
                onChange={(event) =>
                  setFields((previous) => ({ ...previous, [field.key]: event.target.value }))
                }
                rows={3}
                placeholder={t(`request.fields.${field.key}.placeholder`)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-400/50 text-white text-sm placeholder-white/25 focus:outline-none resize-y"
              />
            ) : (
              <input
                type="text"
                value={fields[field.key] || ''}
                onChange={(event) =>
                  setFields((previous) => ({ ...previous, [field.key]: event.target.value }))
                }
                placeholder={t(`request.fields.${field.key}.placeholder`)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-400/50 text-white text-sm placeholder-white/25 focus:outline-none"
              />
            )}
          </div>
        ))}
        <button
          onClick={submit}
          disabled={submitting}
          className="px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {t('request.submit')}
        </button>
      </div>
      {requests.length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/10">
          <h3 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">
            {t('request.yourRequests')}
          </h3>
          <div className="space-y-2">
            {requests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-white/5"
              >
                <span className="text-white/70 text-sm truncate">
                  {request.fields?.experience}
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide flex-shrink-0 ${
                    request.status === 'done'
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'bg-amber-500/15 text-amber-300'
                  }`}
                >
                  {request.status === 'done' ? t('request.statusLive') : t('request.statusInWork')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const PublishedToggle = ({ slug, starter, onUpdated }) => {
  const { t } = useTranslation('dashboard');
  const [enabled, setEnabled] = useState(starter.isPublic !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(starter.isPublic !== false);
  }, [starter.isPublic]);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    try {
      const response = await partnerAPI.updateStarterSettings(slug, starter.starterId, {
        is_public: next,
      });
      const saved = Boolean(response.data.data.isPublic);
      setEnabled(saved);
      onUpdated?.(starter.starterId, saved);
      toast.success(saved ? t('publish.onToast') : t('publish.offToast'));
    } catch (err) {
      console.error('[PartnerDashboard] Publish toggle failed:', err);
      toast.error(t('publish.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-white/10">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium mb-1">
            <Eye size={16} className="text-emerald-400 flex-shrink-0" />
            {t('publish.title')}
          </div>
          <p className="text-white/40 text-xs leading-relaxed">{t('publish.subtitle')}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={saving}
          onClick={toggle}
          className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
            enabled ? 'bg-emerald-500' : 'bg-white/20'
          } ${saving ? 'opacity-60 cursor-wait' : ''}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform flex items-center justify-center ${
              enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          >
            {saving && <Loader2 size={10} className="animate-spin text-gray-500" />}
          </span>
        </button>
      </div>
    </div>
  );
};

const PartnerDashboard = () => {
  const { slug } = useParams();
  const { t } = useTranslation('dashboard');
  const { user, isLoading: authLoading, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [statsRefreshing, setStatsRefreshing] = useState(false);
  const [windowDays, setWindowDays] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [galleryModerated, setGalleryModerated] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Access = admin OR venue owner; the API decides (403 = not your venue).
  const [accessDenied, setAccessDenied] = useState(false);
  const canTry = Boolean(user) && user?.authProvider !== 'guest';
  // Bumped on detach so AddGuidesSection re-fetches its candidates
  const [guidesVersion, setGuidesVersion] = useState(0);
  const [showCreateGuide, setShowCreateGuide] = useState(false);

  useEffect(() => {
    if (authLoading || !canTry) return;
    const load = async () => {
      try {
        const [statsResponse, galleryResponse, reviewsResponse] = await Promise.all([
          partnerAPI.getStats(slug),
          partnerAPI.getGalleryAdmin(slug),
          partnerAPI.getReviews(slug, 8).catch(() => null),
        ]);
        setStats(statsResponse.data.data);
        setGallery(galleryResponse.data.data.images || []);
        setGalleryModerated(Boolean(galleryResponse.data.data.moderated));
        if (reviewsResponse) setReviews(reviewsResponse.data.data);
      } catch (err) {
        console.error('[PartnerDashboard] Failed to load:', err);
        if (err?.response?.status === 403) {
          setAccessDenied(true);
        } else {
          // Stored as i18n key, translated at render (survives language switch)
          setError(err?.response?.status === 404 ? 'auth.notFound' : 'auth.loadFailed');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, authLoading, canTry]);

  const changeWindow = async (days) => {
    setWindowDays(days);
    setStatsRefreshing(true);
    try {
      const response = await partnerAPI.getStats(slug, days);
      setStats(response.data.data);
    } catch (err) {
      console.error('[PartnerDashboard] Stats reload failed:', err);
      toast.error(t('stats.loadFailed'));
    } finally {
      setStatsRefreshing(false);
    }
  };

  const exportCsv = () => {
    if (!stats) return;
    const windowLabel = windowDays
      ? t('csv.windowLastDays', { days: windowDays })
      : t('csv.windowAll');
    const rows = [
      [
        t('csv.guide'),
        t('csv.sessions'),
        t('csv.guestScans'),
        t('csv.chats'),
        t('csv.photos'),
        t('csv.stationsCompleted'),
        t('csv.avgRating'),
        t('csv.reviews'),
      ],
      ...stats.perStarter.map((starter) => [
        starter.name,
        starter.sessions,
        starter.guestSessions,
        starter.chats,
        starter.photos,
        starter.objectivesCompleted ?? 0,
        starter.reviewCount > 0 ? starter.avgRating : '',
        starter.reviewCount ?? 0,
      ]),
      [
        `${t('csv.total')} (${windowLabel})`,
        stats.totals.sessions,
        stats.totals.guest_sessions,
        stats.totals.chats,
        stats.totals.photos,
        stats.totals.objectives_completed ?? 0,
        '',
        '',
      ],
    ];
    const csv = rows
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `okio-${slug}-report${windowDays ? `-${windowDays}d` : ''}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  // Guide created via the shared modal: attach it to this venue right away,
  // then the modal's own navigation drops the owner into the starter editor.
  // If the plan cap blocks the attach, the guide still exists and shows up
  // under "Your other guides" — nothing is lost.
  const handleGuideCreated = async (newProject) => {
    try {
      await partnerAPI.attachStarter(slug, newProject.id);
      toast.success(t('guides.createdAttached', { name: newProject.name }));
    } catch (err) {
      console.error('[PartnerDashboard] Auto-attach after create failed:', err);
      toast.error(err?.response?.data?.detail || t('guides.createdNotAttached'));
    }
  };

  const removeFromPage = async (starterId, name) => {
    if (!window.confirm(t('guides.removeConfirm', { name }))) {
      return;
    }
    try {
      await partnerAPI.detachStarter(slug, starterId);
      toast.success(t('guides.removed'));
      setGuidesVersion((version) => version + 1);
      changeWindow(windowDays);
    } catch (err) {
      console.error('[PartnerDashboard] Detach failed:', err);
      toast.error(err?.response?.data?.detail || t('guides.removeFailed'));
    }
  };

  const moderate = async (mediaId, action) => {
    try {
      const response = await partnerAPI.moderatePhoto(slug, mediaId, action);
      const { approved, hidden } = response.data.data;
      setGallery((previous) =>
        previous.map((image) =>
          image.id === mediaId ? { ...image, approved, hidden } : image
        )
      );
    } catch (err) {
      console.error('[PartnerDashboard] Moderation failed:', err);
      toast.error(t('photos.moderateFailed'));
    }
  };

  const patchStarter = (starterId, patch) => {
    setStats((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        perStarter: previous.perStarter.map((starter) =>
          starter.starterId === starterId ? { ...starter, ...patch } : starter
        ),
      };
    });
  };

  if (authLoading || (canTry && loading)) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-emerald-400/30 border-t-emerald-400 animate-spin" />
      </div>
    );
  }

  if (!canTry) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col">
        <AccountBar user={null} />
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <h1 className="text-white text-xl font-semibold mb-2">{t('auth.signInTitle')}</h1>
          <p className="text-white/50 text-sm mb-4 max-w-sm">{t('auth.signInSubtitle')}</p>
          <div className="flex items-center gap-4">
            <Link
              to={`/login?redirect=/p/${slug}/dashboard`}
              className="px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
            >
              {t('auth.signIn')}
            </Link>
            <Link to={`/p/${slug}`} className="text-emerald-400 text-sm hover:underline">
              {t('auth.viewPublic')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col">
        <AccountBar user={user} onLogout={logout} />
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <h1 className="text-white text-xl font-semibold mb-2">{t('auth.deniedTitle')}</h1>
          <p className="text-white/50 text-sm mb-4 max-w-sm">
            {t('auth.deniedSubtitle', { identity: user?.email || user?.username })}
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={logout}
              className="px-5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors"
            >
              {t('account.signOut')}
            </button>
            <Link to={`/p/${slug}`} className="text-emerald-400 text-sm hover:underline">
              {t('auth.viewPublic')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-8 text-center">
        <h1 className="text-white text-xl font-semibold mb-2">{t(error || 'auth.loadFailed')}</h1>
      </div>
    );
  }

  const { partner, totals, perStarter, budget } = stats;

  const budgetPct =
    budget && budget.sessionBudget > 0
      ? Math.min(100, Math.round((budget.sessionsUsed / budget.sessionBudget) * 100))
      : 0;
  const budgetTone =
    budgetPct >= 90
      ? { bar: 'bg-red-500', text: 'text-red-400' }
      : budgetPct >= 70
        ? { bar: 'bg-amber-400', text: 'text-amber-300' }
        : { bar: 'bg-emerald-500', text: 'text-emerald-400' };
  const budgetResets = budget?.resetsAt
    ? new Date(budget.resetsAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null;
  const tierLabel = budget?.tier
    ? budget.tier.charAt(0).toUpperCase() + budget.tier.slice(1)
    : null;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <AccountBar user={user} onLogout={logout} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              {partner.logoUrl && (
                <img
                  src={getImageUrl(partner.logoUrl)}
                  alt=""
                  className="w-12 h-12 rounded-xl object-contain bg-white flex-shrink-0"
                />
              )}
              <div className="min-w-0">
                <p className="text-emerald-400/80 text-xs font-semibold uppercase tracking-wider mb-1">
                  {t('header.kicker')}
                </p>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{partner.name}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={exportCsv}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
              >
                <FileDown size={13} />
                {t('header.exportCsv')}
              </button>
              <Link
                to={`/p/${slug}`}
                className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
              >
                {t('header.viewPublic')}
              </Link>
            </div>
          </div>
        </header>

        {/* Time window */}
        <div className="flex items-center gap-1 mb-4 bg-white/5 rounded-full p-1 w-fit">
          {TIME_WINDOWS.map(({ days, labelKey }) => (
            <button
              key={labelKey}
              onClick={() => changeWindow(days)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                windowDays === days
                  ? 'bg-emerald-500 text-white'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        {/* Totals */}
        <section
          className={`grid grid-cols-2 sm:grid-cols-5 gap-3 mb-10 transition-opacity ${
            statsRefreshing ? 'opacity-50' : ''
          }`}
        >
          <StatCard icon={BarChart3} label={t('stats.sessions')} value={totals.sessions} />
          <StatCard icon={Users} label={t('stats.guestScans')} value={totals.guest_sessions} />
          <StatCard icon={MessageSquare} label={t('stats.chats')} value={totals.chats} />
          <StatCard icon={Camera} label={t('stats.photos')} value={totals.photos} />
          <StatCard
            icon={Target}
            label={t('stats.stationsDone')}
            value={totals.objectives_completed ?? 0}
          />
        </section>

        {/* Your plan + monthly session meter — always the CURRENT calendar
            month, independent of the stats window above */}
        {budget && (
          <section className="mb-10 rounded-3xl bg-gray-900 border border-white/10 p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider">
                  {t('plan.title')}
                </h2>
                {tierLabel && (
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-semibold">
                    {tierLabel}
                  </span>
                )}
              </div>
              <a
                href="mailto:hello@okio.ai?subject=Plan%20upgrade"
                className="text-white/40 hover:text-white/70 text-xs transition-colors"
              >
                {t('plan.talkToUs')}
              </a>
            </div>

            {budget.sessionBudget > 0 ? (
              <>
                <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                  <p className="text-sm">
                    <span className={`font-semibold ${budgetTone.text}`}>
                      {budget.sessionsUsed.toLocaleString()}
                    </span>{' '}
                    <span className="text-white/50">
                      {t('plan.usageOf', { budget: budget.sessionBudget.toLocaleString() })}
                    </span>
                  </p>
                  {budgetResets && (
                    <p className="text-white/30 text-xs">
                      {t('plan.resets', { date: budgetResets })}
                    </p>
                  )}
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${budgetTone.bar}`}
                    style={{ width: `${budgetPct}%` }}
                  />
                </div>
                {budgetPct >= 90 && (
                  <p className="text-white/50 text-xs mt-3">{t('plan.overBudget')}</p>
                )}
              </>
            ) : (
              <p className="text-white/50 text-sm">{t('plan.unlimited')}</p>
            )}

            <PlanBilling slug={slug} tier={budget?.tier} />
          </section>
        )}

        {/* Venue QR — points at /p/{slug}: the "what shall we do today?"
            poster for hotel lobbies and tourist-info counters. Per-adventure
            QRs below stay the on-location entry. */}
        <section className="mb-10 rounded-3xl bg-gray-900 border border-white/10 p-6">
          <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-1">
            {t('venueQr.title')}
          </h2>
          <p className="text-white/40 text-xs mb-5">{t('venueQr.subtitle')}</p>
          <QrBlock
            slug={slug}
            name={partner.name}
            partnerName="OKIO Scan to Play"
            link={`${window.location.origin}/p/${slug}`}
            posterCta="Scannen &amp; entdecken"
            posterSub="Alle Erlebnisse auf einen Blick &middot; kostenlos &middot; keine App n&ouml;tig"
          >
            <Link
              to={`/p/${slug}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold transition-colors"
            >
              <Play size={13} />
              {t('venueQr.openPage')}
            </Link>
          </QrBlock>
        </section>

        {/* Per-guide: stats + QR + links */}
        <section className="mb-12">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider">
              {t('guides.title')}
            </h2>
            <button
              onClick={() => setShowCreateGuide(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold transition-colors"
            >
              <Plus size={13} />
              {t('guides.create')}
            </button>
          </div>
          <div className="space-y-5">
            {perStarter.map((starter) => (
              <div
                key={starter.starterId}
                className="rounded-3xl bg-gray-900 border border-white/10 p-6"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                  <div className="flex items-center gap-3 min-w-0">
                    {starter.coverImage && (
                      <img
                        src={getImageUrl(starter.coverImage)}
                        alt=""
                        className="w-12 h-12 rounded-xl object-cover flex-shrink-0 bg-gray-800"
                      />
                    )}
                    <h3 className="text-lg font-semibold truncate">{starter.name}</h3>
                    {starter.isPublic === false && (
                      <span
                        title={t('guides.notPublishedHint')}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] font-semibold uppercase tracking-wide flex-shrink-0"
                      >
                        <EyeOff size={10} />
                        {t('guides.notPublished')}
                      </span>
                    )}
                  </div>
                  <div
                    className={`flex gap-4 text-sm text-white/50 transition-opacity ${
                      statsRefreshing ? 'opacity-50' : ''
                    }`}
                  >
                    <span>{t('guides.sessions', { count: starter.sessions })}</span>
                    <span>{t('guides.scans', { count: starter.guestSessions })}</span>
                    <span>{t('guides.photos', { count: starter.photos })}</span>
                    <span>
                      {t('guides.stationsDone', { count: starter.objectivesCompleted ?? 0 })}
                    </span>
                    {starter.reviewCount > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <Star size={13} className="text-amber-400 fill-amber-400" />
                        {starter.avgRating} ({starter.reviewCount})
                      </span>
                    )}
                  </div>
                </div>
                <QrBlock
                  slug={slug}
                  starterId={starter.starterId}
                  name={starter.name}
                  partnerName={partner.name}
                  link={`${window.location.origin}/play/${starter.starterId}`}
                >
                  <Link
                    to={`/play/${starter.starterId}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold transition-colors"
                  >
                    <Play size={13} />
                    {t('guides.testIt')}
                  </Link>
                  <Link
                    to={`/starters/${starter.starterId}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
                  >
                    <Pencil size={13} />
                    {t('guides.edit')}
                  </Link>
                  <button
                    onClick={() => removeFromPage(starter.starterId, starter.name)}
                    title={t('guides.removeHint')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/5 hover:bg-red-500/15 text-white/40 hover:text-red-300 text-xs font-semibold transition-colors"
                  >
                    <Unlink size={13} />
                    {t('guides.removeFromPage')}
                  </button>
                </QrBlock>
                <PublishedToggle
                  slug={slug}
                  starter={starter}
                  onUpdated={(id, isPublic) => patchStarter(id, { isPublic })}
                />
                <AutoImagesToggle
                  slug={slug}
                  starter={starter}
                  entitled={budget?.entitlements?.autoImages !== false}
                  onUpdated={(id, autoInstructionImages) =>
                    patchStarter(id, { autoInstructionImages })
                  }
                />
                <GroupPlayToggle
                  slug={slug}
                  starter={starter}
                  onUpdated={(id, allowCompanionJoin) =>
                    patchStarter(id, { allowCompanionJoin })
                  }
                />
                <VoiceToggle
                  slug={slug}
                  starter={starter}
                  entitled={budget?.entitlements?.voice !== false}
                  onUpdated={(id, patch) => patchStarter(id, patch)}
                />
                <StationFunnel slug={slug} starterId={starter.starterId} />
              </div>
            ))}
            {perStarter.length === 0 && (
              <p className="text-white/40 text-sm py-6 text-center">{t('guides.empty')}</p>
            )}
          </div>
        </section>

        {/* DIY path: attach guides the owner built themselves */}
        <AddGuidesSection
          slug={slug}
          refreshToken={guidesVersion}
          onAttached={() => changeWindow(windowDays)}
        />

        {/* Wizard v0: structured guide intake → concierge fulfillment
            (plan_b2b_self_serve.md §6) */}
        <GuideRequestSection slug={slug} />

        {/* Visitor feedback — the qualitative half of the monthly report */}
        {reviews?.count > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider">
                {t('feedback.title')}
              </h2>
              <span className="inline-flex items-center gap-1.5 text-sm text-white/60">
                <Star size={14} className="text-amber-400 fill-amber-400" />
                <span className="text-white font-semibold">{reviews.average}</span>
                <span className="text-white/40">
                  ({reviews.count} {t('feedback.reviews')})
                </span>
              </span>
            </div>
            {reviews.reviews.length === 0 ? (
              <p className="text-white/40 text-sm py-4">{t('feedback.ratingsOnly')}</p>
            ) : (
              <div className="space-y-3">
                {reviews.reviews.map((review) => (
                  <div
                    key={review.id}
                    className="rounded-2xl bg-gray-900 border border-white/10 p-5"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                      <span className="inline-flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((value) => (
                          <Star
                            key={value}
                            size={12}
                            className={
                              value <= review.rating
                                ? 'text-amber-400 fill-amber-400'
                                : 'text-white/20'
                            }
                          />
                        ))}
                      </span>
                      <span className="text-white/30 text-xs">
                        {review.adventure}
                        {review.createdAt
                          ? ` · ${new Date(review.createdAt).toLocaleDateString()}`
                          : ''}
                      </span>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed mb-1">
                      {review.comment}
                    </p>
                    <p className="text-white/35 text-xs">
                      {review.reviewer}
                      {review.isGuest ? ` · ${t('feedback.guest')}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Gallery moderation */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider">
              {t('photos.title')}
            </h2>
            <span className="text-xs text-white/40">
              {galleryModerated ? t('photos.moderated') : t('photos.unmoderated')}
            </span>
          </div>
          {gallery.length === 0 ? (
            <p className="text-white/40 text-sm py-6 text-center">{t('photos.empty')}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {gallery.map((image) => {
                const isPublic = image.hidden
                  ? false
                  : galleryModerated
                    ? image.approved
                    : true;
                return (
                  <div
                    key={image.id}
                    className={`relative rounded-xl overflow-hidden bg-gray-900 border ${
                      isPublic ? 'border-emerald-500/40' : 'border-white/10'
                    }`}
                  >
                    <img
                      src={getImageUrl(image.url)}
                      alt={image.photographer ? `by ${image.photographer}` : 'Visitor photo'}
                      loading="lazy"
                      className={`aspect-square w-full object-cover ${
                        isPublic ? '' : 'opacity-40'
                      }`}
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 flex items-center justify-between gap-1">
                      <span className="text-[10px] text-white/70 truncate">
                        {image.photographer}
                        {image.isGuest ? ` · ${t('feedback.guest')}` : ''}
                      </span>
                      <div className="flex gap-1 flex-shrink-0">
                        {galleryModerated && !image.hidden && (
                          <button
                            onClick={() =>
                              moderate(image.id, image.approved ? 'unapprove' : 'approve')
                            }
                            title={image.approved ? t('photos.unapprove') : t('photos.approve')}
                            className={`p-1.5 rounded-full transition-colors ${
                              image.approved
                                ? 'bg-emerald-500 text-white'
                                : 'bg-white/20 text-white hover:bg-emerald-500'
                            }`}
                          >
                            <Check size={12} />
                          </button>
                        )}
                        <button
                          onClick={() => moderate(image.id, image.hidden ? 'unhide' : 'hide')}
                          title={image.hidden ? t('photos.unhide') : t('photos.hide')}
                          className={`p-1.5 rounded-full transition-colors ${
                            image.hidden
                              ? 'bg-red-500 text-white'
                              : 'bg-white/20 text-white hover:bg-red-500'
                          }`}
                        >
                          {image.hidden ? <Eye size={12} /> : <EyeOff size={12} />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Same modal as /starters "New Starter" — creates the guide, we
          auto-attach it to this venue, then it navigates into the editor */}
      {showCreateGuide && (
        <EditProjectModal
          key="partner-create-guide-modal"
          isOpen={true}
          onClose={() => setShowCreateGuide(false)}
          project={null}
          mode="create"
          onSuccess={handleGuideCreated}
        />
      )}
    </div>
  );
};

export default PartnerDashboard;
